import { spawnSync } from "node:child_process";
import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { basename, join } from "node:path";
import { buildVersion } from "../lib/build-info.js";

/**
 * Scaffolding for `whop apps deploy`.
 *
 * Instead of shipping a template inside this package, we run TanStack's
 * official create command (`@tanstack/cli create`) with the Cloudflare
 * deployment adapter — always the latest upstream template — and then apply
 * the minimal Whop edits on top: the `whop()` vite plugin (imported from
 * `@whop/cli/vite`), a `deploy` script, and the worker name.
 */

// Windows needs shell resolution for npx/pnpm/bunx (they are .cmd shims).
const SPAWN_VIA_SHELL = process.platform === "win32";

export type PackageManager = "npm" | "pnpm" | "bun" | "yarn";

const DLX_RUNNERS: Record<PackageManager, string[]> = {
	npm: ["npx", "-y"],
	pnpm: ["pnpm", "dlx"],
	bun: ["bunx"],
	yarn: ["yarn", "dlx"],
};

/** Auto-detection order when --pm is not passed. */
export const PM_PRIORITY = [
	"bun",
	"pnpm",
	"npm",
] as const satisfies readonly PackageManager[];

export function isPmInstalled(pm: PackageManager): boolean {
	const result = spawnSync(pm, ["--version"], {
		stdio: "ignore",
		shell: SPAWN_VIA_SHELL,
	});
	return !result.error && result.status === 0;
}

/** First installed package manager, preferring bun, then pnpm, then npm. */
export function detectPackageManager(): PackageManager | null {
	return PM_PRIORITY.find(isPmInstalled) ?? null;
}

export function pmInstallHint(pm: PackageManager): string {
	const win = process.platform === "win32";
	switch (pm) {
		case "bun":
			return win
				? 'powershell -c "irm bun.sh/install.ps1 | iex"'
				: "curl -fsSL https://bun.sh/install | bash";
		case "pnpm":
			return win
				? 'powershell -c "irm get.pnpm.io/install.ps1 | iex"'
				: "curl -fsSL https://get.pnpm.io/install.sh | sh -";
		case "npm":
			return "install Node.js (includes npm): https://nodejs.org";
		case "yarn":
			return "npm install -g yarn";
	}
}

const TANSTACK_CREATE_FLAGS = [
	"--framework",
	"React",
	"--deployment",
	"cloudflare",
	"--no-git",
	"--no-install",
	"--no-intent",
	"--no-toolchain",
	"--no-examples",
	"--non-interactive",
];

/** The exact scaffold command an agent (or human) can run themselves. */
export function tanstackCreateCommand(
	pm: PackageManager,
	projectName: string,
): string {
	return [
		...DLX_RUNNERS[pm],
		"@tanstack/cli@latest",
		"create",
		projectName,
		...TANSTACK_CREATE_FLAGS,
	].join(" ");
}

/** The last lines of a captured child process's output, for error messages. */
export function outputTail(result: {
	stdout: string | null;
	stderr: string | null;
}): string {
	const combined = [result.stdout, result.stderr]
		.filter(Boolean)
		.join("\n")
		.trim();
	if (!combined) return "";
	return `\n\n${combined.split("\n").slice(-15).join("\n")}`;
}

/**
 * Output is captured, not streamed: the template's own banner and "next
 * steps" would conflict with the deploy flow's single summary at the end.
 * It resurfaces in the error message when the create fails.
 */
export function runTanstackCreate(options: {
	targetDir: string;
	projectName: string;
	pm: PackageManager;
}): void {
	const [runner, ...runnerArgs] = DLX_RUNNERS[options.pm];
	const result = spawnSync(
		runner!,
		[
			...runnerArgs,
			"@tanstack/cli@latest",
			"create",
			options.projectName,
			"--target-dir",
			options.targetDir,
			...TANSTACK_CREATE_FLAGS,
		],
		{
			stdio: ["ignore", "pipe", "pipe"],
			encoding: "utf-8",
			shell: SPAWN_VIA_SHELL,
		},
	);
	if (result.error) throw result.error;
	if (result.status !== 0) {
		throw new Error(
			`\`${options.pm === "npm" ? "npx" : options.pm} @tanstack/cli create\` failed (exit ${result.status}). Check your network connection and try again.${outputTail(result)}`,
		);
	}
}

const VITE_CONFIG_NAMES = [
	"vite.config.ts",
	"vite.config.mts",
	"vite.config.js",
	"vite.config.mjs",
];

export function findViteConfig(dir: string): string | undefined {
	return VITE_CONFIG_NAMES.map((name) => join(dir, name)).find((path) =>
		existsSync(path),
	);
}

const WRANGLER_CONFIG_NAMES = ["wrangler.jsonc", "wrangler.json"];

function cliDependencyVersion(): string {
	return buildVersion ? `^${buildVersion}` : "latest";
}

/**
 * Wires a Vite project for Whop hosting: the whop() pack plugin, deploy
 * script, @whop/cli devDependency, and the worker name. Works on any Vite
 * project, not just fresh scaffolds — every edit is idempotent, so re-running
 * after fixing something is safe.
 *
 * Anything that can't be applied automatically is returned as a manual step:
 * a self-contained instruction precise enough for an agent (or human) to
 * finish the whopification by hand.
 */
export function whopifyProject(options: { targetDir: string; route: string }): {
	manualSteps: string[];
} {
	const manualSteps: string[] = [];
	patchPackageJson(options.targetDir, manualSteps);
	patchViteConfig(options.targetDir, manualSteps);
	patchWranglerConfig(options.targetDir, options.route, manualSteps);
	return { manualSteps };
}

function patchPackageJson(dir: string, manualSteps: string[]): void {
	const file = join(dir, "package.json");
	let pkg: {
		scripts?: Record<string, string>;
		devDependencies?: Record<string, string>;
	};
	try {
		pkg = JSON.parse(readFileSync(file, "utf-8")) as typeof pkg;
	} catch {
		manualSteps.push(
			`package.json could not be parsed. Add a "deploy": "whop apps deploy" script, a "typecheck" script, and a devDependency "@whop/cli": "${cliDependencyVersion()}".`,
		);
		return;
	}

	pkg.scripts = {
		...pkg.scripts,
		deploy: "whop apps deploy",
		typecheck: pkg.scripts?.typecheck ?? "tsc --noEmit",
	};
	pkg.devDependencies = {
		...pkg.devDependencies,
		// The project imports the whop() vite plugin from @whop/cli/vite.
		"@whop/cli": pkg.devDependencies?.["@whop/cli"] ?? cliDependencyVersion(),
	};

	writeFileSync(file, `${JSON.stringify(pkg, null, 2)}\n`);
}

function patchViteConfig(dir: string, manualSteps: string[]): void {
	const file = findViteConfig(dir);
	if (!file) {
		manualSteps.push(
			"No vite config found (looked for vite.config.ts/.mts/.js/.mjs). Whop hosting serves Vite builds — create a vite config that builds with @cloudflare/vite-plugin (emitting dist/client and, for SSR, dist/server) and includes the whop() plugin from @whop/cli/vite in its plugins array.",
		);
		return;
	}

	let content = readFileSync(file, "utf-8");
	const hasWhopPlugin = content.includes("@whop/cli/vite");

	if (!hasWhopPlugin) {
		const pluginsOpen = /plugins:\s*\[/;
		if (!pluginsOpen.test(content)) {
			manualSteps.push(
				`Could not find a \`plugins: [...]\` array in ${basename(file)}. Add \`import { whop } from '@whop/cli/vite'\` and include \`whop()\` in the config's plugins so builds pack dist/whop-build.zip.`,
			);
		} else {
			content = `import { whop } from '@whop/cli/vite'\n${content}`;
			content = content.replace(
				pluginsOpen,
				(match) => `${match}\n    whop(),`,
			);
			writeFileSync(file, content);
		}
	}

	if (!content.includes("@cloudflare/vite-plugin")) {
		manualSteps.push(
			`${basename(file)} does not use @cloudflare/vite-plugin. Whop hosting runs builds on Cloudflare Workers: install @cloudflare/vite-plugin and wrangler, add \`cloudflare({ viteEnvironment: { name: 'ssr' } })\` to the plugins array (before your framework plugin), and make sure \`vite build\` emits dist/client (static assets) and dist/server (worker modules with an index.js entry).`,
		);
	}
}

function patchWranglerConfig(
	dir: string,
	route: string,
	manualSteps: string[],
): void {
	const file = WRANGLER_CONFIG_NAMES.map((name) => join(dir, name)).find(
		(path) => existsSync(path),
	);
	if (!file) {
		manualSteps.push(
			`No wrangler config found. Create wrangler.jsonc with: { "name": "${route}", "compatibility_date": "<today>", "compatibility_flags": ["nodejs_compat"], "main": "<your framework's server entry>" } (for TanStack Start the main is "@tanstack/react-start/server-entry").`,
		);
		return;
	}

	const content = readFileSync(file, "utf-8");
	const updated = replaceTopLevelName(content, route);
	if (updated) {
		writeFileSync(file, updated);
	} else {
		manualSteps.push(
			`Set the top-level "name" in ${basename(file)} to "${route}" so the worker is named after the app's route.`,
		);
	}
}

// Rewrites the top-level "name" key's value in a wrangler.json(c) document.
// A plain first-match regex could hit a nested "name" (e.g. a kv_namespaces
// entry) or a string value, so this walks the document skipping strings and
// JSONC comments while tracking nesting depth. Returns null when no top-level
// name key exists.
function replaceTopLevelName(content: string, route: string): string | null {
	let depth = 0;
	let i = 0;
	while (i < content.length) {
		const ch = content[i];
		if (ch === '"') {
			let j = i + 1;
			while (j < content.length && content[j] !== '"') {
				j += content[j] === "\\" ? 2 : 1;
			}
			if (depth === 1 && content.slice(i + 1, j) === "name") {
				const after = content.slice(j + 1);
				const value = after.match(/^(\s*:\s*)"(?:[^"\\]|\\.)*"/);
				if (value) {
					const start = j + 1 + value[1]!.length;
					const end = j + 1 + value[0]!.length;
					return `${content.slice(0, start)}"${route}"${content.slice(end)}`;
				}
			}
			i = j + 1;
			continue;
		}
		if (ch === "/" && content[i + 1] === "/") {
			const newline = content.indexOf("\n", i);
			i = newline === -1 ? content.length : newline + 1;
			continue;
		}
		if (ch === "/" && content[i + 1] === "*") {
			const close = content.indexOf("*/", i + 2);
			i = close === -1 ? content.length : close + 2;
			continue;
		}
		if (ch === "{" || ch === "[") depth++;
		if (ch === "}" || ch === "]") depth--;
		i++;
	}
	return null;
}
